/**
 * Horizontal grid — a slider only when the row overflows.
 *
 * Every block arrives as a grid (render.php, and the stylesheet's default
 * state). This decides, per block and per width, whether that grid should be
 * a slider instead: it should when there are more items than the row holds,
 * and only then. A block whose items all fit is left as the grid it is — no
 * arrows, no dots, no drag — and a block that stops fitting after a resize
 * becomes a slider then.
 *
 * Two modes, one rule. "Display as slider" means the rule runs at every
 * width. Off, the block is a wrapping grid and the rule runs only on a phone,
 * where a row is two items and a grid of ten would be five rows of
 * thumbnails — so it collapses to a slider there, for the room.
 */

import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide-core.min.css';

/**
 * How many items a row holds at the current width — and so Splide's perPage
 * when it mounts. Viewport tiers rather than a container query, because they
 * have to agree with three things that are all viewport-based: Splide's own
 * breakpoints, the grid's column counts in _horizontal-grid.scss, and the
 * theme's tablet breakpoint the "View All" toggle keys off.
 */
const TIERS = [
	{ query: window.matchMedia( '(min-width: 1024px)' ), perPage: 6 },
	{ query: window.matchMedia( '(min-width: 768px)' ), perPage: 4 },
];
const PHONE_PER_PAGE = 2;

/**
 * @return {number} Items per row now.
 */
function perPageNow() {
	const tier = TIERS.find( ( t ) => t.query.matches );

	return tier ? tier.perPage : PHONE_PER_PAGE;
}

/**
 * The live Splide instance per block root.
 *
 * A WeakMap rather than a data attribute because the instance itself is what
 * unmounting needs, and because "is this mounted" has to be answerable after
 * a teardown without an attribute somebody forgot to unstamp.
 *
 * @type {WeakMap<HTMLElement, Splide>}
 */
const sliders = new WeakMap();

/** Every block root on the page, for the resize pass. */
const roots = [];

/**
 * Text the toggle shows while the grid is open. Printed by render.php through
 * wp_add_inline_script so it is translatable; the fallback only runs if that
 * print is missing.
 */
const showLess = ( window.dnHgrid && window.dnHgrid.showLess ) || 'Show Less';

let idCounter = 0;

/**
 * Where "View All" is allowed. On a phone the covers only ever run as a
 * slider — a grid of two columns is not worth a button — so the stylesheet
 * hides the toggle below this width and this closes a grid that was open
 * when the viewport crossed the line. 768 is the theme's tablet breakpoint
 * (abstracts/_variables.scss).
 */
const gridAllowed = window.matchMedia( '(min-width: 768px)' );

/**
 * Move Splide's generated pagination list into the controls row.
 *
 * Splide's Elements component only discovers `.splide__pagination` inside its
 * own root (`.dn-hgrid__viewport.splide`), via a plain `root.querySelector()`.
 * The markup here deliberately puts the dots in the sibling controls row
 * instead — between the two arrow buttons — so arrows sit at the ends and
 * dots sit centred between them, per the approved design. Splide never finds
 * that div, so it builds its own `<ul>` (carrying the classes this file
 * requested via `classes.pagination`) and appends it inside the viewport.
 * This relocates that generated list into the controls row every time it is
 * (re)built — including on breakpoint changes, which destroy and recreate it.
 *
 * @param {HTMLElement}      controls The controls row.
 * @param {HTMLUListElement} list     Splide's generated pagination list.
 */
function relocateDots( controls, list ) {
	if ( ! controls || ! list ) {
		return;
	}

	const placeholder = controls.querySelector( '.dn-hgrid__dots' );
	const nextArrow = controls.querySelector( '[data-dir="next"]' );

	if ( placeholder ) {
		placeholder.replaceWith( list );
	} else if ( nextArrow ) {
		controls.insertBefore( list, nextArrow );
	} else {
		controls.appendChild( list );
	}
}

/**
 * Mount Splide on one root and switch the stylesheet to the sliding state.
 *
 * Safe to call again after unmountSlider(): everything it adds, Splide's
 * destroy(true) takes away again, and the one thing it adds itself — the
 * splide__slide class on each item — is idempotent.
 *
 * @param {HTMLElement} root Block root.
 */
function mountSlider( root ) {
	if ( sliders.has( root ) ) {
		return;
	}

	const viewport = root.querySelector( '.splide' );
	const list = root.querySelector( '.splide__list' );
	const controls = root.querySelector( '.dn-hgrid__controls' );

	if ( ! viewport || ! list ) {
		return;
	}

	// The class first: it is what gives the viewport, the track and the list
	// their boxes back (they are display: contents in the grid state), and
	// Splide measures them on mount.
	root.classList.add( 'is-sliding' );

	/*
	 * ACF renders inner blocks as one string, so it cannot put splide__slide on
	 * each child — that has to happen here. Splide reads the class when it mounts,
	 * so this must run first.
	 */
	Array.from( list.children ).forEach( ( child ) => {
		child.classList.add( 'splide__slide' );
	} );

	const perMove = Math.max( 1, parseInt( root.dataset.dnAdvance, 10 ) || 1 );
	const reduced = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	const splide = new Splide( viewport, {
		// 'loop' clones enough slides to fill the viewport on both sides, so the
		// last item runs straight into the first and neither arrow ever disables.
		// 'slide' is the default and stops at each end. Splide falls back to
		// 'slide' on its own if there are too few slides to clone.
		type: '1' === root.dataset.dnLoop ? 'loop' : 'slide',
		perPage: 6,
		perMove,
		gap: '30px',
		arrows: false,
		pagination: true,
		// Splide's own arrow-key handling defaults to *off* (`keyboard: false`) —
		// confirmed against the 4.1.4 source, where `options.keyboard` has no
		// fallback and the Keyboard component's `if ( keyboard )` guard is simply
		// falsy until set. 'focused' binds the listener to this slider's root
		// rather than the whole window, so arrow keys move slides only while
		// something inside this slider has focus.
		keyboard: 'focused',
		speed: reduced ? 0 : 400,
		// Splide's own class hooks would fight ours; point it at the markup that
		// already exists rather than letting it generate its own. (It still builds
		// its own element rather than reusing ours — see relocateDots() above.)
		// This is what puts dn-hgrid__dots on the <ul> Splide generates, not on a
		// wrapper around it, so the SCSS selector
		// `.dn-hgrid__dots .splide__pagination__page` matches with no wrapper div
		// involved. The server-rendered `.dn-hgrid__dots` div is *replaced* by
		// that <ul> in relocateDots(), never filled — do not "fix" this into a
		// wrapper-div assumption, or the dots go silently unstyled.
		classes: { pagination: 'dn-hgrid__dots splide__pagination' },
		// The same tiers as perPageNow() and the grid's columns, so a slider
		// that stays mounted across a tier change shows the row the grid would.
		breakpoints: {
			1023: { perPage: 4 },
			767: { perPage: PHONE_PER_PAGE },
		},
	} );

	// isOverflow: Splide reports whether the slides actually exceed the viewport.
	// Controls stay hidden when they all fit, so a short grid never offers arrows
	// that cannot move. Confirmed against the installed 4.1.4 source: Layout emits
	// this synchronously during mount() (undefined -> boolean always differs, so
	// the first emit is guaranteed), and Components.Layout.isOverflow() is a plain,
	// unmangled property on the public Components map in this version's dist build.
	// The mount rule above already guarantees overflow, so this is a belt to
	// that brace — a rounding case at a tier edge, say.
	splide.on( 'overflow', ( isOverflow ) => {
		if ( controls ) {
			controls.hidden = ! isOverflow;
		}
	} );

	// pagination:mounted fires once on initial mount and again on every rebuild
	// (breakpoint changes emit `updated`, which the Pagination component treats as
	// a re-mount). Re-running the relocation each time keeps the dots in the
	// controls row regardless of how many times Splide rebuilds them.
	splide.on( 'pagination:mounted', ( data ) => {
		relocateDots( controls, data.list );
	} );

	splide.mount();
	sliders.set( root, splide );

	if ( controls ) {
		controls.hidden = ! splide.Components.Layout.isOverflow();
	}
}

/**
 * Take one slider down and return the root to the grid state.
 *
 * destroy(true) is Splide's "completely": it removes the clones, the inline
 * transform and widths, every class and attribute it added, and the pagination
 * list it built — so the items are plain children of .dn-hgrid__items again,
 * ready to be laid out by the grid or mounted afresh.
 *
 * @param {HTMLElement} root Block root.
 */
function unmountSlider( root ) {
	const splide = sliders.get( root );

	if ( ! splide ) {
		return;
	}

	splide.destroy( true );
	sliders.delete( root );

	// The one class destroy(true) leaves behind — checked against the 4.1.4
	// source: mount() adds it and nothing in Elements.destroy() lists it. It is
	// not cosmetic. splide-core carries `.splide.is-initialized:not(.is-active)
	// .splide__list { display: block }` at 0,4,0, which outranks the
	// display: contents the grid state depends on, and the items stacked one
	// to a row until this was measured.
	const viewport = root.querySelector( '.splide' );

	if ( viewport ) {
		viewport.classList.remove( 'is-initialized' );
	}

	const controls = root.querySelector( '.dn-hgrid__controls' );

	if ( controls ) {
		controls.hidden = true;
	}

	root.classList.remove( 'is-sliding' );
}

/**
 * The arrows. One listener per root, for the life of the page, looking the
 * instance up at click time — so a slider that has been torn down and mounted
 * again is still driven by the same listener, rather than by a stale closure
 * over an instance destroy() has already emptied.
 *
 * @param {HTMLElement} root Block root.
 */
function bindArrows( root ) {
	const controls = root.querySelector( '.dn-hgrid__controls' );

	if ( ! controls ) {
		return;
	}

	controls.addEventListener( 'click', ( event ) => {
		const button = event.target.closest( '[data-dir]' );
		const splide = sliders.get( root );

		if ( ! button || ! splide ) {
			return;
		}

		splide.go( 'next' === button.dataset.dir ? '>' : '<' );
	} );
}

/**
 * Decide, for one root, whether it should be a slider right now, and make it
 * so. The whole policy is here:
 *
 * - it FITS when the row holds every item, at which point there is nothing to
 *   slide and the grid stands;
 * - grid mode may only slide on a phone; slider mode may slide at any width;
 * - a grid the visitor opened with "View All" stays open (is-expanded), and
 *   that flag is cleared elsewhere the moment the viewport becomes a phone's.
 *
 * Idempotent, and cheap when nothing changed, so it is safe to run on every
 * resize tick.
 *
 * @param {HTMLElement} root Block root.
 */
function apply( root ) {
	const count = parseInt( root.dataset.dnCount, 10 ) || 0;
	const perPage = perPageNow();
	const fits = count <= perPage;
	const phone = ! gridAllowed.matches;
	const maySlide = root.classList.contains( 'dn-hgrid--slider' ) || phone;
	const expanded = root.classList.contains( 'is-expanded' );

	// Read by the stylesheet: a "View All" beside a grid that already fits
	// has nothing to open, so it is hidden.
	root.classList.toggle( 'is-fitted', fits );

	if ( maySlide && ! fits && ! expanded ) {
		mountSlider( root );
	} else {
		unmountSlider( root );
	}
}

/**
 * The slider the toggle belongs to: the first horizontal grid inside the
 * nearest ancestor that holds one. Walks up rather than assuming a section
 * class, so the button works inside whatever group an author put it in,
 * provided the grid is in there with it.
 *
 * @param {HTMLElement} toggle The .dn-hgrid-toggle wrapper.
 * @return {HTMLElement|null} The grid root, or null when there is none.
 */
function findGrid( toggle ) {
	let scope = toggle.parentElement;

	while ( scope && scope !== document.body ) {
		const grid = scope.querySelector( '.dn-hgrid' );

		if ( grid ) {
			return grid;
		}

		scope = scope.parentElement;
	}

	return null;
}

/**
 * Make sure the toggle's control is a real <button>.
 *
 * The pattern ships one (core/button with tagName "button"), but a copy saved
 * before that, or a button an author made a toggle by adding the class in
 * Advanced, is an <a href="#">. A link that does not navigate is wrong for a
 * screen reader and for the keyboard — Space does nothing on a link — so it is
 * replaced with a button carrying the same classes and label.
 *
 * @param {HTMLElement} toggle The .dn-hgrid-toggle wrapper.
 * @return {HTMLButtonElement|null}
 */
function ensureButton( toggle ) {
	const control = toggle.querySelector( '.wp-block-button__link' );

	if ( ! control ) {
		return null;
	}

	if ( 'BUTTON' === control.tagName ) {
		return control;
	}

	const button = document.createElement( 'button' );
	button.type = 'button';
	button.className = control.className;
	button.setAttribute( 'style', control.getAttribute( 'style' ) || '' );
	button.innerHTML = control.innerHTML;
	control.replaceWith( button );

	return button;
}

/**
 * Wire one "View All" toggle to its grid.
 *
 * Nothing is persisted: the page loads as the editor set it, the visitor opens
 * the grid, and a reload closes it again. That is the brief — a way to see
 * everything at once, not a preference.
 *
 * @param {HTMLElement} toggle The .dn-hgrid-toggle wrapper.
 */
function initToggle( toggle ) {
	const grid = findGrid( toggle );

	// No slider to open — the grid is already the grid, or there is none. The
	// stylesheet hides the wrapper in that case too, so this only matters when
	// the button sits somewhere the :has() selector does not reach.
	if ( ! grid || ! grid.classList.contains( 'dn-hgrid--slider' ) ) {
		toggle.hidden = true;
		return;
	}

	const button = ensureButton( toggle );

	if ( ! button ) {
		return;
	}

	if ( ! grid.id ) {
		idCounter += 1;
		grid.id = `dn-hgrid-${ idCounter }`;
	}

	const viewAll = button.textContent;

	button.setAttribute( 'aria-expanded', 'false' );
	button.setAttribute( 'aria-controls', grid.id );

	/**
	 * @param {boolean} open Whether the grid should be open.
	 */
	const setOpen = ( open ) => {
		if ( open === grid.classList.contains( 'is-expanded' ) ) {
			return;
		}

		grid.classList.toggle( 'is-expanded', open );
		apply( grid );

		button.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
		button.textContent = open ? showLess : viewAll;
	};

	button.addEventListener( 'click', () => {
		setOpen( ! grid.classList.contains( 'is-expanded' ) && gridAllowed.matches );
	} );

	// Back to the slider the moment the viewport becomes a phone's, whatever
	// state the visitor left it in. Nothing to do on the way up: the grid is
	// something the visitor asks for, not something a wider screen implies.
	gridAllowed.addEventListener( 'change', ( event ) => {
		if ( ! event.matches ) {
			setOpen( false );
		}
	} );
}

/**
 * Every root, re-decided after the viewport settles. 150ms is long enough to
 * collapse a drag-resize into one pass and short enough that an orientation
 * change reads as immediate. The tier edges are what matter, and matchMedia
 * would report those on its own — but a rotation or a zoom can cross one
 * without a clean change event, and this catches everything.
 */
function bindResize() {
	let timer = 0;

	window.addEventListener( 'resize', () => {
		window.clearTimeout( timer );
		timer = window.setTimeout( () => roots.forEach( apply ), 150 );
	} );
}

function init() {
	document.querySelectorAll( '.dn-hgrid' ).forEach( ( root ) => {
		const list = root.querySelector( '.dn-hgrid__items' );

		if ( ! list ) {
			return;
		}

		// Counted once, before any mount: Splide's loop mode clones slides into
		// the same list, and a count taken later would include them.
		root.dataset.dnCount = String( list.children.length );

		roots.push( root );
		bindArrows( root );
		apply( root );
	} );

	document.querySelectorAll( '.dn-hgrid-toggle' ).forEach( initToggle );
	bindResize();
}

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}

export { mountSlider, unmountSlider };
